// src/context/ShopContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

// --- Configuration ---
const API_BASE_URL = 'http://localhost:8080/api/cart';
// 🛑 IMPORTANT: यह एक डमी यूजर ID है। इसे आपको अपने असली Auth Context से बदलना होगा।
const DUMMY_USER_ID = 1; 

// --- Interfaces for Frontend ---

// ✅ EXPORTED: CartRequest DTO से मैप किया गया इंटरफ़ेस
export interface CartItem {
  id: number; // cartId (ProductCart.id)
  userId: number;
  productId: number;
  productType: string;
  quantity: number;
  
  // GoldMaster/SilverMaster से
  title: string; // itemName
  price: number; // price (BigDecimal JS में number बन जाएगा)
  image: string; // imageUrl
  // अन्य फ़ील्ड्स (category, purity, आदि) भी आप चाहें तो जोड़ सकते हैं
}

// Context Type Definition
interface ShopContextType {
  cartItems: CartItem[];
  cartLoading: boolean; // Loading state जोड़ा गया
  addItemToCart: (productId: number, productType: string, quantity: number, itemName: string) => Promise<void>;
  removeItemFromCart: (cartId: number) => Promise<void>;
  updateItemQuantity: (cartId: number, newQuantity: number) => Promise<void>;
  wishlistItems: any[]; // Dummy wishlist
}

// Default/Initial Context Value
const ShopContext = createContext<ShopContextType | undefined>(undefined);

// Custom hook to use the context
export const useShop = () => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

interface ShopProviderProps {
    children: ReactNode;
}

// --- Provider Component ---

export const ShopProvider: React.FC<ShopProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [wishlistItems, setWishlistItems] = useState<any[]>([]); // Mock wishlist
  const [cartLoading, setCartLoading] = useState(true);

  // Function to fetch cart items from backend
  const fetchCartItems = useCallback(async () => {
    setCartLoading(true);
    try {
      // GET call to CartController.java /api/cart/{userId}
      // response.data CartRequest[] DTO होगा
      const response = await axios.get<any[]>(`${API_BASE_URL}/${DUMMY_USER_ID}`);
      
      // CartRequest DTO से CartItem frontend type में map करें
      const fetchedItems: CartItem[] = response.data.map(item => ({
        id: item.cartId, // backend से cartId आ रहा है
        userId: item.userId,
        productId: item.productId,
        productType: item.productType,
        quantity: item.quantity,
        title: item.itemName,
        // price BigDecimal है, JS इसे number/string के रूप में हैंडल करेगा
        price: parseFloat(item.price), 
        image: item.imageUrl,
      }));

      setCartItems(fetchedItems); 

    } catch (error) {
      console.error("Error fetching cart items:", error);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // Effect to fetch items on mount
  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  // --- Add Item Logic ---
  const addItemToCart = async (productId: number, productType: string, quantity: number, itemName: string) => {
    try {
      const requestBody = {
        userId: DUMMY_USER_ID,
        productId: productId,
        productType: productType,
        quantity: quantity,
      };
      
      // POST call to CartController.java /api/cart/add
      await axios.post(`${API_BASE_URL}/add`, requestBody);
      
      // Success message (as requested)
      // alert(`✅ successfully added ${itemName} to cart.`);
      
      // Cart items को re-fetch करें
      await fetchCartItems(); 

    } catch (error) {
      console.error("Error adding to cart:", error);
      // alert("❌ Error adding item to cart. Please try again.");
    }
  };

  // --- Remove Item Logic ---
  const removeItemFromCart = async (cartId: number) => {
    if (window.confirm("do you want to remove this item from the cart?")) {
        try {
            // DELETE call to CartController.java /api/cart/{cartId}
            await axios.delete(`${API_BASE_URL}/${cartId}`);
            
            // Success message
            // alert(`✅ Item removed from cart successfully.`);
            
            // Cart items को re-fetch करें
            await fetchCartItems();

        } catch (error) {
            console.error("Error removing from cart:", error);
            alert("❌ error removing item from cart. Please try again.");
        }
    }
  };

  const updateItemQuantity = useCallback(async (cartId: number, newQuantity: number) => {
        if (newQuantity < 1) {
     
            if (window.confirm("Do you want to remove this item from the cart?")) {
                await removeItemFromCart(cartId);
            }
            return;
        }

        try {
       
            const response = await axios.put(`${API_BASE_URL}/update-quantity`, null, {
                params: {
                    cartId: cartId,
                    quantity: newQuantity,
                }
            });

            // Local state update: Find the item by cartId and update its quantity
            setCartItems(prevItems => prevItems.map(item => 
                item.id === cartId ? { ...item, quantity: newQuantity } : item
            ));
            
            console.log("Quantity updated successfully:", response.data);
            // Optionally, show a toast/alert
            // alert(`Quantity for item ${cartId} updated to ${newQuantity}`);

        } catch (error) {
            console.error("Error updating item quantity:", error);
            // Re-fetch the cart to sync local state with backend on error
            fetchCartItems(); 
            // alert("Failed to update item quantity. Please try again.");
        }
    }, [fetchCartItems, removeItemFromCart]); // removeItemFromCart dependency added

  return (
    <ShopContext.Provider value={{ cartItems, cartLoading, addItemToCart, removeItemFromCart, wishlistItems, updateItemQuantity }}>
      {children}
    </ShopContext.Provider>
  );
};